import { computeCoordinateExtents } from "utils/math/computeCoordinateExtents";
import { routeGroupLabelOf } from "components/SmartSurveyor/shared/routeGroupLabelOf";
import { bulkAddPlanRoutes } from "../../../utils/bulkAddPlanRoutes";
import { moveRouteBetweenGroups } from "../../../utils/moveRouteBetweenGroups";
import { renameRouteGroupInPlan } from "../../../utils/renameRouteGroupInPlan";
import { setCableTypeNameStatusInPlan } from "../../../utils/setCableTypeNameStatusInPlan";
import { setRouteGroupLvMatchInPlan } from "../../../utils/setRouteGroupLvMatchInPlan";
import { LV_MATCH_COLUMNS } from "./constants";
import { pageIndexOf } from "./planPatches";
import type { BulkAddPlanRouteEntry } from "../../../utils/bulkAddPlanRoutes/types";
import type { PlanV3, ScaledRoute } from "../../../utils/types";
import type { RouteChange, RouteSnapshot } from "../../types";
import type { PlanPatch } from "./types";

const isRetyped = ({ before, after }: RouteChange): boolean =>
  before !== null && after !== null && before.routeTypeId !== after.routeTypeId;

const sumMeters = (routes: ScaledRoute[]): number =>
  routes.reduce((sum, route) => sum + (Number(route.meters) || 0), 0);

const hasRouteGroup = (plan: PlanV3, label: string): boolean =>
  plan.routeGroups.some((group) => group.label === label);

const withGeometryOf = (
  route: ScaledRoute,
  snapshot: RouteSnapshot,
): ScaledRoute => ({
  ...route,
  nodes: snapshot.nodes,
  edges: snapshot.edges,
  distance_px: snapshot.distancePx ?? 0,
  meters: snapshot.meters ?? 0,
  coordinateExtents: computeCoordinateExtents(snapshot.nodes),
});

// Status and LV come from a route already in the group; a new group gets them in applyRouteTypeChanges.
const toScaledRoute = (
  rowId: number,
  snapshot: RouteSnapshot,
  groupLabel: string,
  pageIndex: number,
  sibling: ScaledRoute | undefined,
): ScaledRoute => ({
  route_type_id: snapshot.routeTypeId,
  id: String(rowId),
  name: snapshot.name ?? "",
  page: pageIndex,
  breaker: snapshot.breaker ?? "",
  cable_type: groupLabel,
  cable_type_status: sibling?.cable_type_status ?? "confident",
  lv_position: sibling?.lv_position ?? "",
  lvMatch: sibling?.lvMatch,
  note: "",
  distance_px: snapshot.distancePx ?? 0,
  meters: snapshot.meters ?? 0,
  nodes: snapshot.nodes,
  edges: snapshot.edges,
  creation_source: snapshot.creationSource,
  coordinateExtents: computeCoordinateExtents(snapshot.nodes),
});

export const removeDeletedRoutes: PlanPatch = ({
  plan,
  routes,
  routeGroupLabelByRouteId,
}) => {
  const deletedIds = new Set(
    routes
      .filter((change) => change.changeType === "deleted")
      .map((change) => String(change.rowId)),
  );
  if (deletedIds.size === 0) return plan;
  for (const routeId of deletedIds) {
    if (!routeGroupLabelByRouteId.has(routeId)) return null;
  }

  const routeGroups = plan.routeGroups.flatMap((group) => {
    const keptRoutes = group.routes.filter(
      (route) => !deletedIds.has(route.id),
    );
    if (keptRoutes.length === group.routes.length) return [group];
    if (keptRoutes.length === 0) return [];
    return [{ ...group, routes: keptRoutes, meters: sumMeters(keptRoutes) }];
  });
  return { ...plan, routeGroups };
};

// A merge moves all of a type's routes, but a plan holds few routes, so moving them one by one stays cheap.
export const moveRoutes: PlanPatch = ({
  plan,
  routes,
  routeLabelByTypeId,
  routeGroupLabelByRouteId,
}) => {
  let next = plan;
  for (const change of routes) {
    if (change.after === null || !isRetyped(change)) continue;
    const routeId = String(change.rowId);
    const fromGroupLabel = routeGroupLabelByRouteId.get(routeId);
    const toGroupLabel = routeLabelByTypeId.get(change.after.routeTypeId);
    if (fromGroupLabel === undefined || toGroupLabel === undefined) {
      return null;
    }
    next = moveRouteBetweenGroups({
      plan: next,
      routeId,
      fromGroupLabel,
      toGroupLabel,
      newRouteTypeId: change.after.routeTypeId,
    });
  }
  return next;
};

export const addCreatedRoutes: PlanPatch = ({
  plan,
  routes,
  routeLabelByTypeId,
  routeGroupLabelByRouteId,
}) => {
  const siblingByLabel = new Map(
    plan.routeGroups.map((group) => [group.label, group.routes[0]]),
  );
  const entries: BulkAddPlanRouteEntry[] = [];

  for (const { changeType, rowId, after } of routes) {
    if (changeType !== "created" || after === null) continue;
    // Already shown means a stale plan; adding it again would duplicate it.
    if (routeGroupLabelByRouteId.has(String(rowId))) return null;

    const groupLabel = routeLabelByTypeId.get(after.routeTypeId);
    const pageIndex = pageIndexOf(plan, after.pageId);
    if (groupLabel === undefined || pageIndex === null) return null;

    entries.push({
      groupLabel,
      routeTypeId: after.routeTypeId,
      scaledRoute: toScaledRoute(
        rowId,
        after,
        groupLabel,
        pageIndex,
        siblingByLabel.get(groupLabel),
      ),
    });
  }

  return entries.length === 0
    ? plan
    : bulkAddPlanRoutes({ plan, routes: entries });
};

// Runs after moveRoutes, so a retyped route is already in its new group.
export const editRoutes: PlanPatch = ({
  plan,
  routes,
  routeGroupLabelByRouteId,
}) => {
  const editedById = new Map<string, RouteSnapshot>();
  for (const { changeType, rowId, after } of routes) {
    if (changeType === "updated" && after !== null) {
      editedById.set(String(rowId), after);
    }
  }
  if (editedById.size === 0) return plan;
  for (const routeId of editedById.keys()) {
    if (!routeGroupLabelByRouteId.has(routeId)) return null;
  }

  const routeGroups = plan.routeGroups.map((group) => {
    // Untouched groups keep their identity, so they do not re-render.
    if (!group.routes.some((route) => editedById.has(route.id))) return group;
    const editedRoutes = group.routes.map((route) => {
      const snapshot = editedById.get(route.id);
      return snapshot === undefined ? route : withGeometryOf(route, snapshot);
    });
    return { ...group, routes: editedRoutes, meters: sumMeters(editedRoutes) };
  });
  return { ...plan, routeGroups };
};

export const applyRouteTypeChanges: PlanPatch = ({ plan, routeTypes }) => {
  let next = plan;
  for (const { changeType, rowId, before, after } of routeTypes) {
    // Removing its routes already dropped the group; a group still shown means a stale plan.
    if (changeType === "deleted") {
      const isStillShown =
        before !== null &&
        hasRouteGroup(next, routeGroupLabelOf(before.cableType));
      if (isStillShown) return null;
      continue;
    }
    if (after === null) continue;
    const groupLabel = routeGroupLabelOf(after.cableType);
    if (changeType === "updated" && before !== null) {
      next = renameRouteGroupInPlan({
        plan: next,
        oldGroupLabel: routeGroupLabelOf(before.cableType),
        newGroupLabel: groupLabel,
        cableTypeStatus: after.cableTypeStatus,
        routeTypeId: rowId,
      });
    }
    // A group exists only while it has routes; a restored type without any needs a reload.
    if (changeType === "created" && !hasRouteGroup(next, groupLabel)) {
      return null;
    }

    next = setCableTypeNameStatusInPlan({
      plan: next,
      routeTypeIds: [rowId],
      cableTypeStatus: after.cableTypeStatus,
    });
    // A restored type's routes were built without its LV, so it is always written.
    const isLvMatchChanged =
      before === null ||
      LV_MATCH_COLUMNS.some((column) => before[column] !== after[column]);
    if (isLvMatchChanged) {
      next = setRouteGroupLvMatchInPlan({
        plan: next,
        routeGroupLabel: groupLabel,
        lvMatch: {
          lvId: after.lvMatchId ?? undefined,
          lvName: after.lvMatchName ?? undefined,
          reasoning: after.lvMatchReasoning ?? undefined,
        },
      });
    }
  }
  return next;
};
